// =============================================================
// Shared scene components - used across the home page and elsewhere
// =============================================================

// A miniature transcript card
function MiniTranscript({ lines = [], live = true, lang = 'en' }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)',
      padding: '16px',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {live && (
        <div style={{display:'flex', alignItems:'center', gap:8, fontSize:11, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', color:'var(--text-soft)'}}>
          <span style={{width:7, height:7, borderRadius:'50%', background:'var(--teal)', boxShadow:'0 0 0 3px var(--teal-wash)'}}></span>
          Listening
        </div>
      )}
      {lines.map((l, i) => (
        <div key={i} style={{display:'flex', gap:8, alignItems:'flex-start'}}>
          <span className={"hd-speaker sp-" + l.speaker}>{l.speaker}</span>
          <div style={{fontSize:13.5, lineHeight:1.5, color:'var(--text)'}}>
            {l.text}
            {l.translation && <div style={{fontSize:12.5, color:'var(--text-soft)', fontStyle:'italic', marginTop:3}}>→ {l.translation}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// CDT chip block - for the "Code" feature art
function CdtChip({ code, name, sub, fee, attachments = [], status = 'ready', dark = false }) {
  const surface = dark ? '#172238' : '#fff';
  const borderC = dark ? 'rgba(255,255,255,0.08)' : 'var(--border)';
  const textC = dark ? '#E9EEF5' : 'var(--text)';
  const subC = dark ? '#94A3B8' : 'var(--text-muted)';
  return (
    <div style={{
      background: surface,
      border: '1px solid ' + borderC,
      borderRadius: 18,
      overflow: 'hidden',
      boxShadow: dark ? '0 12px 30px -10px rgba(0,0,0,0.4)' : '0 12px 30px -10px rgba(15,23,42,0.1)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        display:'flex', alignItems:'stretch',
        borderBottom: '1px solid ' + borderC,
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #22C0DA, #0F7689)',
          color:'#fff',
          padding:'16px 22px',
          display:'flex', flexDirection:'column', justifyContent:'center', gap:4,
          minWidth: 110,
        }}>
          <div style={{fontFamily:'var(--font-mono)', fontSize:20, fontWeight:700, letterSpacing:'-0.01em'}}>{code}</div>
          <div style={{fontSize:10, opacity:0.85, letterSpacing:'0.05em', textTransform:'uppercase'}}>CDT 2026</div>
        </div>
        <div style={{padding:'16px 22px', display:'flex', flexDirection:'column', gap:4, flex:1, minWidth:0}}>
          <div style={{fontSize:14, fontWeight:600, color:textC, letterSpacing:'-0.005em'}}>{name}</div>
          <div style={{fontSize:12.5, color:subC}}>{sub}</div>
        </div>
        <div style={{padding:'16px 22px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'flex-end', gap:4, minWidth:90}}>
          <div style={{fontFamily:'var(--font-mono)', fontSize:15, fontWeight:600, color:textC}}>{fee}</div>
          <div style={{fontSize:10.5, color:subC, letterSpacing:'0.04em', textTransform:'uppercase'}}>Fee · NW Dental</div>
        </div>
      </div>
      <div style={{padding:'14px 22px', display:'flex', flexDirection:'column', gap:10, background: dark ? 'rgba(255,255,255,0.02)' : 'var(--surface-subtle)'}}>
        <div style={{fontSize:11, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', color:subC}}>Required attachments</div>
        <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
          {attachments.map((a, i) => (
            <span key={i} style={{
              display:'inline-flex', alignItems:'center', gap:6,
              padding:'4px 10px', borderRadius:999,
              background: a.ok ? (dark ? 'rgba(25,173,197,0.18)' : 'var(--teal-wash)') : (dark ? 'rgba(180,83,9,0.2)' : 'var(--warning-bg)'),
              color: a.ok ? (dark ? '#6BD7E8' : 'var(--teal-deep)') : (dark ? '#FBBF24' : 'var(--warning)'),
              fontSize:11.5, fontWeight:500,
              border: '1px solid ' + (a.ok ? (dark ? 'rgba(25,173,197,0.3)' : 'rgba(25,173,197,0.2)') : (dark ? 'rgba(180,83,9,0.3)' : 'rgba(180,83,9,0.2)')),
            }}>
              {a.ok ? '✓' : '○'} {a.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Mini perio cell grid
function PerioGrid({ teeth = [13,14,15,16], compact = false }) {
  // sample data
  const data = {
    13: { M: 3, B: 2, D: 4 },
    14: { M: 5, B: 3, D: 6 },
    15: { M: 8, B: 4, D: 5 },
    16: { M: 4, B: 3, D: 3 },
  };
  const bopSet = new Set(['14-D','15-M','15-D','13-D']);
  return (
    <div style={{display:'flex', flexDirection:'column', gap:6}}>
      <div style={{display:'grid', gridTemplateColumns:'32px repeat('+teeth.length+', 1fr)', gap:4}}>
        <div></div>
        {teeth.map(n => <div key={n} style={{textAlign:'center', fontSize:11, color:'var(--text-soft)', fontFamily:'var(--font-mono)', fontWeight:600}}>#{n}</div>)}
      </div>
      {['M','B','D'].map(site => (
        <div key={site} style={{display:'grid', gridTemplateColumns:'32px repeat('+teeth.length+', 1fr)', gap:4}}>
          <div style={{fontSize:11, color:'var(--text-soft)', fontWeight:600, display:'grid', placeItems:'center'}}>{site}</div>
          {teeth.map(n => {
            const d = data[n][site];
            const hasBop = bopSet.has(n+'-'+site);
            let bg='#fff', color='var(--text-soft)';
            if (d>=6){bg='#FEE2E2';color='#B91C1C';}
            else if(d>=4){bg='#FEF3C7';color='#B45309';}
            else {bg='#DCFCE7';color='#15803D';}
            return (
              <div key={n} style={{
                height: compact ? 28 : 32, borderRadius: 6, background: bg, color,
                border:'1px solid var(--border)',
                display:'grid', placeItems:'center',
                fontFamily:'var(--font-mono)', fontSize:12.5, fontWeight:600,
                position:'relative',
              }}>
                {d}
                {hasBop && <span style={{position:'absolute', top:2, right:3, width:5, height:5, borderRadius:'50%', background:'#DC2626'}}></span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Claim card for the bill section
function ClaimCard() {
  return (
    <div style={{
      background:'#fff',
      border:'1px solid var(--border)',
      borderRadius:'var(--r-lg)',
      padding:'22px 24px',
      boxShadow:'var(--shadow-md)',
      display:'flex', flexDirection:'column', gap:16,
    }}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <div style={{fontSize:11, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', fontWeight:600}}>Claim #DC-204819</div>
          <div style={{fontSize:18, fontWeight:600, marginTop:2, letterSpacing:'-0.01em'}}>Aetna PPO · Hannah Vo</div>
        </div>
        <span className="chip chip-success" style={{height:28}}>✓ Submitted</span>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, borderTop:'1px solid var(--border)'}}>
        <div style={{padding:'14px 0', borderRight:'1px solid var(--border)', paddingRight:16}}>
          <div style={{fontSize:11, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', fontWeight:600, marginBottom:4}}>Date of service</div>
          <div style={{fontSize:14, fontFamily:'var(--font-mono)'}}>2026-05-18</div>
        </div>
        <div style={{padding:'14px 0', paddingLeft:16}}>
          <div style={{fontSize:11, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', fontWeight:600, marginBottom:4}}>Provider</div>
          <div style={{fontSize:14}}>Dr. Sasha Patel</div>
        </div>
      </div>

      <div style={{display:'flex', flexDirection:'column', gap:10, borderTop:'1px solid var(--border)', paddingTop:14}}>
        <div style={{fontSize:11, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', fontWeight:600}}>Procedures</div>
        {[
          { code:'D4341', name:'SRP - 4+ teeth, UR quadrant', fee:'$258.00' },
          { code:'D0274', name:'Bitewing radiographs - four films', fee:'$78.00' },
          { code:'D9215', name:'Local anesthesia', fee:'$32.00' },
        ].map(p => (
          <div key={p.code} style={{display:'grid', gridTemplateColumns:'auto 1fr auto', gap:12, alignItems:'center', padding:'8px 12px', background:'var(--surface-subtle)', borderRadius:8}}>
            <span className="hd-cdt-code">{p.code}</span>
            <span style={{fontSize:13}}>{p.name}</span>
            <span style={{fontFamily:'var(--font-mono)', fontSize:13, fontWeight:600}}>{p.fee}</span>
          </div>
        ))}
      </div>

      <div style={{display:'flex', justifyContent:'space-between', paddingTop:14, borderTop:'1px solid var(--border)', alignItems:'center'}}>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <span className="chip chip-teal" style={{fontSize:11}}>✓ PA radiograph</span>
          <span className="chip chip-teal" style={{fontSize:11}}>✓ Perio chart</span>
          <span className="chip chip-teal" style={{fontSize:11}}>✓ Narrative drafted</span>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:11, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', fontWeight:600}}>Total</div>
          <div style={{fontSize:20, fontFamily:'var(--font-mono)', fontWeight:600, color:'var(--teal-deep)'}}>$368.00</div>
        </div>
      </div>
    </div>
  );
}

// Expose
Object.assign(window, { MiniTranscript, CdtChip, PerioGrid, ClaimCard });
